import type { SelectFieldOption } from '@/features/proposals/hooks/form-fields';
import type { InstallmentType } from '@/services';

export type CapitalGlobalPremiumPeriodicityValue =
  | 'monthly'
  | 'bimonthly'
  | 'quarterly'
  | 'fourmonthly'
  | 'semiannual'
  | 'annual';

export const INSTALLMENT_COUNT_TO_PREMIUM_PERIODICITY: Record<
  number,
  CapitalGlobalPremiumPeriodicityValue
> = {
  12: 'monthly',
  6: 'bimonthly',
  4: 'quarterly',
  3: 'fourmonthly',
  2: 'semiannual',
  1: 'annual',
};

export type CapitalGlobalPremiumPeriodicityOption = SelectFieldOption & {
  value: CapitalGlobalPremiumPeriodicityValue;
  installmentCount: number;
  firstInstallmentCode: number;
};

export function buildPremiumPeriodicityOptionsFromCatalog(
  installmentTypes: InstallmentType[]
): CapitalGlobalPremiumPeriodicityOption[] {
  return installmentTypes
    .map(type => {
      const installmentCount = Number(type.installmentPeriodCount);
      const value = INSTALLMENT_COUNT_TO_PREMIUM_PERIODICITY[installmentCount];
      if (!value) return null;

      const firstInstallmentCode = Number(type.code);
      if (!Number.isFinite(firstInstallmentCode)) return null;

      return {
        value,
        label: type.description,
        installmentCount,
        firstInstallmentCode,
      };
    })
    .filter((item): item is CapitalGlobalPremiumPeriodicityOption => item != null)
    .sort((a, b) => b.installmentCount - a.installmentCount);
}

export function findPremiumPeriodicityOption(
  options: CapitalGlobalPremiumPeriodicityOption[],
  value: string
): CapitalGlobalPremiumPeriodicityOption | undefined {
  return options.find(item => item.value === value);
}

export function resolveInstallmentCountFromPremiumPeriodicity(
  value: string,
  options: CapitalGlobalPremiumPeriodicityOption[] = []
): number | null {
  const fromCatalog = findPremiumPeriodicityOption(options, value);
  if (fromCatalog) return fromCatalog.installmentCount;

  for (const [count, periodicity] of Object.entries(
    INSTALLMENT_COUNT_TO_PREMIUM_PERIODICITY
  )) {
    if (periodicity === value) return Number(count);
  }

  return null;
}
